import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useLocation, Outlet } from 'react-router-dom';
import { Box, Button, FormControl, MenuItem, Select, Stack, Tab, Tabs, Typography } from '@mui/material';
import db from '../../../../firebase-config';
import { buildBaselineParams } from './components/baselineParams';

const alertLevelColor = (level) => {
  if (level === 'RED') return '#f44336';
  if (level === 'YELLOW') return '#ffc107';
  if (level === 'GREEN') return '#4caf50';
  return null;
};

const formatHistoryDate = (dateText) => {
  if (!dateText) return '--';
  const [year, month, day] = dateText.split('-');
  return `${day}/${month}/${year}`;
};

const formatHistoryDateTime = (bucketTime) => {
  if (!bucketTime) return 'Latest';
  return `${formatHistoryDate(bucketTime.slice(0, 10))} ${bucketTime.slice(11, 16)}`;
};

const NO_SKU_VALUE = '__NO_SKU__';

const getHistorySku = (row) => {
  const sku = String(row?.SKU ?? '').trim();
  return sku || NO_SKU_VALUE;
};

const SigmaTrackGiveawayTab = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { bu = '', plant = '', factory = '', line = '' } = useParams();

  const base = `/${bu}/${plant}/${factory}/${line}/Sigma Track`;
  const currentPage = location.pathname.endsWith('/2') ? 1 : 0;

  const handleChange = (_, newValue) =>
    navigate(`${base}/${newValue === 0 ? '1' : '2'}`);

  // ── Firestore real-time listener ──────────────────────────────────────────
  const [latestData, setLatestData] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);
  const [viewMode, setViewMode] = useState('latest');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedBucketEpoch, setSelectedBucketEpoch] = useState('');
  const [selectedSku, setSelectedSku] = useState('');
  const historyFeatureEnabled = true;

  useEffect(() => {
    if (!plant || !line) return;
    const countryPrefix = 'Thailand';
    const fsFactory = plant === '4117' || plant === '4071' || plant === '4116' ? '00000000000000' : factory;
    const docId = `${countryPrefix}${plant}${fsFactory}${line}`;
    const unsub = db
      .collection('prod_sigma_track_recipe_giveaway_v2')
      .doc(docId)
      .onSnapshot((snap) => {
        console.log('[Firestore] docId:', docId, '| exists:', snap.exists);
        if (snap.exists) {
          setLatestData(snap.data());
        }
      }, (err) => console.error('[Firestore] error:', err.code, err.message));
    return () => unsub();
  }, [plant, line]);

  useEffect(() => {
    if (!plant || !line) return;
    const countryPrefix = 'Thailand';
    const fsFactory = plant === '4117' || plant === '4071' || plant === '4116' ? '00000000000000' : factory;
    const prefix = `${countryPrefix}${plant}${fsFactory}${line}`;
    const unsub = db
      .collection('prod_sigma_track_recipe_giveaway_30d_v2')
      .where('PREFIX', '==', prefix)
      .onSnapshot((snap) => {
        const rows = snap.docs
          .map((doc) => doc.data())
          .filter(Boolean)
          .sort((a, b) => (Number(b.BUCKET_EPOCH) || 0) - (Number(a.BUCKET_EPOCH) || 0));
        setHistoryRows(rows);
      }, (err) => console.error('[Sigma History] error:', err.code, err.message));
    return () => unsub();
  }, [plant, factory, line]);

  // const availableDates = useMemo(() => (
  //   [...new Set(historyRows.map((row) => row?.BUCKET_TIME?.slice(0, 10)).filter(Boolean))]
  // ), [historyRows]);

  // useEffect(() => {
  //   if (!availableDates.length) {
  //     setSelectedDate('');
  //     return;
  //   }
  //   if (!selectedDate || !availableDates.includes(selectedDate)) {
  //     setSelectedDate(availableDates[0]);
  //   }
  // }, [availableDates, selectedDate]);

  // const rowsForDate = useMemo(() => (
  //   historyRows.filter((row) => row?.BUCKET_TIME?.slice(0, 10) === selectedDate)
  // ), [historyRows, selectedDate]);

  // useEffect(() => {
  //   if (!historyFeatureEnabled) return;
  //   if (!availableDates.length) {
  //     setSelectedDate('');
  //     setSelectedBucketEpoch('');
  //     return;
  //   }
  //   if (!selectedDate || !availableDates.includes(selectedDate)) {
  //     setSelectedDate(availableDates[0]);
  //   }
  // }, [availableDates, historyFeatureEnabled, selectedDate]);

  // useEffect(() => {
  //   if (viewMode !== 'history') return;
  //   if (!rowsForDate.length) {
  //     setSelectedBucketEpoch('');
  //     return;
  //   }
  //   const exists = rowsForDate.some((row) => String(row?.BUCKET_EPOCH) === String(selectedBucketEpoch));
  //   if (!selectedBucketEpoch || !exists) {
  //     setSelectedBucketEpoch(String(rowsForDate[0].BUCKET_EPOCH));
  //   }
  // }, [rowsForDate, selectedBucketEpoch, viewMode]);
  const availableSkus = useMemo(() => (
    [...new Set(historyRows.map(getHistorySku))]
  ), [historyRows]);

  useEffect(() => {
    if (!availableSkus.length) {
      setSelectedSku('');
      return;
    }
    if (!availableSkus.includes(selectedSku)) {
      setSelectedSku(availableSkus[0]);
    }
  }, [availableSkus, selectedSku]);

  const rowsForSku = useMemo(() => (
    historyRows.filter(
      (row) => getHistorySku(row) === selectedSku
    )
  ), [historyRows, selectedSku]);

  const availableDates = useMemo(() => (
    [...new Set(
      rowsForSku
        .map((row) => row?.BUCKET_TIME?.slice(0, 10))
        .filter(Boolean)
    )]
  ), [rowsForSku]);

  useEffect(() => {
    if (!availableDates.length) {
      setSelectedDate('');
      setSelectedBucketEpoch('');
      return;
    }

    if (!availableDates.includes(selectedDate)) {
      setSelectedDate(availableDates[0]);
    }
  }, [availableDates, selectedDate]);

  const rowsForDate = useMemo(() => (
    rowsForSku.filter(
      (row) => row?.BUCKET_TIME?.slice(0, 10) === selectedDate
    )
  ), [rowsForSku, selectedDate]);

  useEffect(() => {
    if (viewMode !== 'history') return;

    if (!rowsForDate.length) {
      setSelectedBucketEpoch('');
      return;
    }

    const exists = rowsForDate.some(
      (row) => String(row?.BUCKET_EPOCH) === selectedBucketEpoch
    );

    if (!exists) {
      setSelectedBucketEpoch(String(rowsForDate[0].BUCKET_EPOCH));
    }
  }, [rowsForDate, selectedBucketEpoch, viewMode]);

  const selectedHistoryData = useMemo(() => (
    rowsForDate.find((row) => String(row?.BUCKET_EPOCH) === String(selectedBucketEpoch)) ?? null
  ), [rowsForDate, selectedBucketEpoch]);

  //const fsData = viewMode === 'history' ? (selectedHistoryData ?? latestData) : latestData;
  const fsData = viewMode === 'history'
    ? selectedHistoryData
    : latestData;
  const rawWeights = Array.isArray(fsData?.RAW_WEIGHTS_JSON) ? fsData.RAW_WEIGHTS_JSON : [];

  // ── Spec + control limits (from Firestore) ──────────────────────────────
  const lsl = fsData?.LSL ?? 0;
  const usl = fsData?.USL ?? 0;
  const std = fsData?.LSL ?? 0;  // std ≈ LSL (target = lower spec)
  const ucl = fsData?.UCL_60 ?? null;
  const lcl = fsData?.LCL_60 ?? null;
  const meanX = fsData?.CL ?? /*fsData?.X_BAR_60 ??*/ null;
  const sigma = fsData?.SIGMA_60 ?? 0;
  const processCapChart = fsData?.PROCESS_CAP_CHART ?? []


  // ── Derived values ───────────────────────────────────────────────────────
  const plantName = fsData
    ? [fsData.PLANT_ID, /^0+$/.test(fsData.FACTORY ?? '') ? null : fsData.FACTORY, fsData.LINE]
      .filter(Boolean).join(' — ')
    : '';
  const lineName = fsData?.LINE ?? line ?? '';
  const skuLabel = fsData?.SKU ?? '';

  const totalPack = fsData?.N_5 ?? 0;
  const avgWeight = totalPack > 0 ? (fsData?.X_I_5 ?? 0) : 0;
  const cpk = fsData?.CPK_5 ?? 0;
  const ppk = fsData?.PPK_5 ?? 0;
  const cpk5 = totalPack > 0 ? (fsData?.CPK_5 ?? 0) : 0;
  const ppk5 = totalPack > 0 ? (fsData?.PPK_5 ?? 0) : 0;
  const cpk60 = fsData?.CPK_60 ?? cpk5;
  const ppk60 = fsData?.PPK_60 ?? ppk5;
  const sigma5 = totalPack > 0 ? (fsData?.SIGMA_5 ?? 0) : 0;
  const detectedPack = fsData?.DETECTED_5 ?? 0;
  const upperPack = fsData?.UPPER_DETECTED_5 ?? 0;
  const onSpecPack = Math.max(0, totalPack - detectedPack - upperPack);
  const shift = fsData?.SHIFT ?? '';
  const factoryDate = fsData?.FACTORY_DATE ?? '';
  const alertLevel = fsData?.ALERT_LEVEL ?? null;
  const alertTrigger = fsData?.ALERT_TRIGGER ?? null;
  const alertColor = alertLevelColor(alertLevel);

  const giveawayPct = parseFloat((fsData?.GIVEAWAY_PCT ?? 0).toFixed(2));
  const giveawayKg = parseFloat((fsData?.GIVEAWAY_KG ?? 0).toFixed(2));
  const giveawayTarget = parseFloat((fsData?.TARGET_GIVEAWAY ?? 1).toFixed(1));

  const timeRange = fsData?.BUCKET_TIME
    ? (() => {
      const start = fsData.BUCKET_TIME.slice(11, 16);
      const [h, m] = start.split(':').map(Number);
      const endH = String(m + 5 >= 60 ? h + 1 : h).padStart(2, '0');
      const endM = String((m + 5) % 60).padStart(2, '0');
      return `${start} - ${endH}:${endM}`;
    })()
    : '--:-- - --:--';

  const hasMetricData = !!fsData && (Number(totalPack) > 0 || Number(avgWeight) > 0 || Number(sigma5) > 0 || Number(cpk5) > 0 || Number(ppk5) > 0 || rawWeights.length > 0);

  const fallbackSuggestionInline = (() => {
    if (!hasMetricData) return '';
    if (cpk5 >= 1.33 && ppk5 >= 1.33) return 'กระบวนการปกติ - ควบคุมดีมาก';
    if (cpk5 >= 1.0 && ppk5 >= 1.0) return 'กระบวนการอยู่ในเกณฑ์ยอมรับได้';
    if (cpk5 >= 0.67) return 'เฝ้าระวังน้ำหนัก - ความผันผวนเริ่มเพิ่ม';
    return 'น้ำหนักไม่คงที่ - ตรวจสอบกระบวนการทันที';
  })();

  const fallbackSuggestion = (() => {
    if (!hasMetricData) return '';
    if (cpk5 >= 1.33 && ppk5 >= 1.33) return 'กระบวนการอยู่ในการควบคุมดีมาก (Cpk, Ppk ≥ 1.33)';
    if (cpk5 >= 1.0 && ppk5 >= 1.0) return 'กระบวนการอยู่ในการควบคุม (Cpk, Ppk ≥ 1.0)';
    if (cpk5 >= 0.67) return 'Cpk, Ppk < 1 : กระบวนการยังคุมได้ แต่มีความผันผวน ให้เฝ้าระวังน้ำหนัก';
    return 'Cpk < 0.67 : กระบวนการไม่อยู่ในการควบคุม ให้ดำเนินการแก้ไข';
  })();

  const suggestionInline = fsData?.SUGGESTION_INLINE ?? fallbackSuggestionInline;
  const showSuggestionInline = !!suggestionInline;
  const suggestionWeb = fsData?.SUGGESTION_WEB ?? fallbackSuggestion;
  const showSuggestionWeb = !!suggestionWeb;
  // const viewingLabel = viewMode === 'history' && selectedHistoryData
  //   ? `กำลังดูย้อนหลัง: ${formatHistoryDateTime(selectedHistoryData.BUCKET_TIME)}`
  //   : 'กำลังดูล่าสุด';
  const viewingLabel = viewMode === 'history'
    ? selectedHistoryData
      ? `กำลังดูย้อนหลัง: ${selectedSku === NO_SKU_VALUE ? 'ไม่มี SKU' : selectedSku
      } | ${formatHistoryDateTime(selectedHistoryData.BUCKET_TIME)}`
      : 'ไม่มีข้อมูลย้อนหลังสำหรับรายการที่เลือก'
    : 'กำลังดูล่าสุด';

  const outletContext = {
    lsl, usl, std, ucl, lcl, meanX, sigma, processCapChart,
    fsData, rawWeights,
    avgWeight, cpk, ppk, cpk5, ppk5, cpk60, ppk60, sigma5, totalPack, onSpecPack, upperPack, detectedPack,
    giveawayPct, giveawayKg, giveawayTarget,
    timeRange, skuLabel, lineName, plantName,
    alertLevel, alertTrigger, alertColor,
    suggestionInline, showSuggestionInline,
    suggestionWeb, showSuggestionWeb,
    viewMode,
    viewingLabel,
    baselineParams: buildBaselineParams(fsData),
    baselineLabel: `${fsData?.FACTORY ?? ''} · ${lineName} · ${skuLabel}`,
    // Map the real field when supplied; no fabricated production shift count.
    baselineCurrentShiftCount: null,
    shift, factoryDate,
  };

  return (
    <Box sx={{ bgcolor: '#000', minHeight: 'calc(100vh - 64px)', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ borderBottom: '1px solid #1a1a1a' }}>
        <Tabs
          value={currentPage}
          onChange={handleChange}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': {
              minHeight: 36,
              fontSize: 12,
              fontFamily: "'CPF Imm Sook', sans-serif",
              color: '#888',
              letterSpacing: 1,
              border: '1px solid transparent',
            },
            '& .Mui-selected': {
              color: '#fff !important',
              border: '1px solid #f44336',
            },
            '& .MuiTabs-indicator': { display: 'none' },
          }}
        >
          <Tab label="SIGMA TRACK PAGE 1" />
          <Tab label="SIGMA TRACK PAGE 2" />
        </Tabs>
      </Box>
      <Box sx={{ borderBottom: '1px solid #1a1a1a', px: 2, py: 1 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Button
              size="small"
              variant={viewMode === 'latest' ? 'contained' : 'outlined'}
              color="error"
              onClick={() => setViewMode('latest')}
            >
              ล่าสุด
            </Button>
            <Button
              size="small"
              variant={viewMode === 'history' ? 'contained' : 'outlined'}
              color="warning"
              onClick={() => setViewMode('history')}
              disabled={!historyFeatureEnabled}
            >
              ดูย้อนหลัง
            </Button>
            {viewMode === 'history' && historyFeatureEnabled && (
              <>
                <FormControl
                  size="small"
                  sx={{ minWidth: 200 }}
                  disabled={!availableSkus.length}
                >
                  <Select
                    value={selectedSku}
                    displayEmpty
                    inputProps={{ 'aria-label': 'เลือก SKU' }}
                    onChange={(e) => {
                      setSelectedSku(e.target.value);
                      setSelectedDate('');
                      setSelectedBucketEpoch('');
                    }}
                    sx={{
                      color: '#fff',
                      '.MuiOutlinedInput-notchedOutline': {
                        borderColor: '#444',
                      },
                      '.MuiSelect-icon': {
                        color: '#fff',
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      เลือก SKU
                    </MenuItem>

                    {availableSkus.map((sku) => (
                      <MenuItem key={sku} value={sku}>
                        {sku === NO_SKU_VALUE ? 'ไม่มี SKU' : sku}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 150 }} disabled={!availableDates.length}>
                  <Select
                    value={selectedDate}
                    displayEmpty
                    onChange={(e) => {
                      setSelectedDate(e.target.value);
                      setSelectedBucketEpoch('');
                    }}
                    sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}
                  >
                    <MenuItem value="" disabled>
                      เลือกวันที่
                    </MenuItem>
                    {availableDates.map((dateValue) => (
                      <MenuItem key={dateValue} value={dateValue}>
                        {formatHistoryDate(dateValue)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 140 }} disabled={!rowsForDate.length}>
                  <Select
                    value={selectedBucketEpoch}
                    displayEmpty
                    onChange={(e) => setSelectedBucketEpoch(e.target.value)}
                    sx={{ color: '#fff', '.MuiOutlinedInput-notchedOutline': { borderColor: '#444' } }}
                  >
                    <MenuItem value="" disabled>
                      เลือกรอบเวลา
                    </MenuItem>
                    {rowsForDate.map((row) => (
                      <MenuItem key={row.BUCKET_EPOCH} value={String(row.BUCKET_EPOCH)}>
                        {row.BUCKET_TIME?.slice(11, 16) ?? '--:--'}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button size="small" variant="text" color="inherit" onClick={() => setViewMode('latest')}>
                  กลับสู่ล่าสุด
                </Button>
              </>
            )}
          </Stack>
          <Typography sx={{ color: '#aaa', fontSize: 13, fontFamily: "'CPF Imm Sook', sans-serif" }}>
            {viewingLabel}
          </Typography>
        </Stack>
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden' }}>
        <Outlet context={outletContext} />
      </Box>
    </Box>
  );
};

export default SigmaTrackGiveawayTab;
